// Setup helpers that judge the actual image: is the whole rider in view, can
// both feet be seen, and is the rider holding still long enough to capture
// the target stance.

import { LM, lmVis, lmX, lmY, NUM_LANDMARKS, type PoseFrame } from '../core/pose';

export interface FramingCheck {
  person: boolean;
  head: boolean;
  hands: boolean;
  frontFoot: boolean;
  rearFoot: boolean;
  /** Some foot point is in frame: the rider is out at the board, not up close to the phone. */
  feetInFrame: boolean;
  /** One actionable hint, or null when framing is good. */
  hint: string | null;
  ok: boolean;
}

const V = 0.5;
const inside = (lm: Float32Array, i: number, m = 0.015) =>
  lmX(lm, i) > m && lmX(lm, i) < 1 - m && lmY(lm, i) > m && lmY(lm, i) < 1 - m;
const seen = (lm: Float32Array, i: number) => lmVis(lm, i) >= V && inside(lm, i);

/**
 * `frontIsLeft` decides which detected foot is reported as the front foot
 * (regular stance: left foot forward).
 */
export function framingCheck(lm: Float32Array | null, frontIsLeft: boolean): FramingCheck {
  if (!lm) return { person: false, head: false, hands: false, frontFoot: false, rearFoot: false, feetInFrame: false, hint: 'Step into view of the camera.', ok: false };
  const head = [LM.nose, LM.leftEar, LM.rightEar].some((i) => seen(lm, i));
  const hands = seen(lm, LM.leftWrist) && seen(lm, LM.rightWrist);
  const foot = (left: boolean) =>
    left ? seen(lm, LM.leftHeel) && seen(lm, LM.leftToe) : seen(lm, LM.rightHeel) && seen(lm, LM.rightToe);
  const frontFoot = foot(frontIsLeft);
  const rearFoot = foot(!frontIsLeft);
  const feetInFrame = [LM.leftAnkle, LM.rightAnkle, LM.leftHeel, LM.rightHeel].some((i) => seen(lm, i));

  let y0 = 1, y1 = 0, x0 = 1, x1 = 0;
  for (let i = 0; i < NUM_LANDMARKS; i++) {
    if (lmVis(lm, i) < 0.3) continue;
    y0 = Math.min(y0, lmY(lm, i));
    y1 = Math.max(y1, lmY(lm, i));
    x0 = Math.min(x0, lmX(lm, i));
    x1 = Math.max(x1, lmX(lm, i));
  }
  let hint: string | null = null;
  const lowFeet = [LM.leftHeel, LM.rightHeel, LM.leftToe, LM.rightToe].some((i) => lmY(lm, i) > 0.97);
  if (y1 - y0 > 0.9 || (y0 < 0.02 && lowFeet)) hint = 'Move the phone further away so your whole body fits.';
  else if (lowFeet) hint = 'Your feet are at the bottom edge. Tilt the phone down slightly or move it back.';
  else if (y0 < 0.02) hint = 'Your head is cut off. Tilt the phone up slightly or move it back.';
  else if (x0 < 0.02 || x1 > 0.98) hint = 'You’re at the edge of the picture. Centre the board in the frame.';
  else if (!frontFoot || !rearFoot) hint = `I can’t see your ${!frontFoot && !rearFoot ? 'feet' : !frontFoot ? 'front foot' : 'back foot'} clearly. Try a higher or more diagonal camera angle.`;
  else if (!head) hint = 'Your head isn’t visible. Make sure your whole body is in view.';
  else if (!hands) hint = 'Keep your hands in view too.';
  else if (y1 - y0 < 0.3) hint = 'You look small in the picture. Move the phone a little closer.';
  return { person: true, head, hands, frontFoot, rearFoot, feetInFrame, hint, ok: head && frontFoot && rearFoot && hint === null };
}

/**
 * Detects when the rider has held still with both feet visible for
 * `holdMs`, and returns those frames.
 */
export class StillnessDetector {
  private buf: PoseFrame[] = [];
  constructor(private readonly holdMs = 2000, private readonly tolerance = 0.012) {}

  reset() {
    this.buf = [];
  }

  /** Feeds a frame; returns the still window once complete. Progress 0..1 via `progress()`. */
  push(f: PoseFrame): PoseFrame[] | null {
    const lm = f.lm;
    const feet = [LM.leftHeel, LM.leftToe, LM.rightHeel, LM.rightToe];
    if (!lm || feet.some((i) => lmVis(lm, i) < V)) {
      this.buf = [];
      return null;
    }
    this.buf.push(f);
    // Drop leading frames that differ from the newest pose: the window restarts on movement.
    const ref = lm;
    while (this.buf.length > 1) {
      const first = this.buf[0].lm!;
      const moved = feet.some((i) => Math.hypot(lmX(first, i) - lmX(ref, i), lmY(first, i) - lmY(ref, i)) > this.tolerance * 2.5);
      if (!moved) break;
      this.buf.shift();
    }
    if (f.t - this.buf[0].t >= this.holdMs) {
      const out = this.buf;
      this.buf = [];
      return out;
    }
    return null;
  }

  progress(now: number): number {
    if (!this.buf.length) return 0;
    return Math.min(1, (now - this.buf[0].t) / this.holdMs);
  }
}

/** During a slow test pop-up: how often each foot was visible once the rider was up. */
export class VisibilityAudit {
  private frames = 0;
  private front = 0;
  private rear = 0;
  private head = 0;

  constructor(private readonly frontIsLeft: boolean) {}

  push(lm: Float32Array | null) {
    this.frames++;
    if (!lm) return;
    const c = framingCheck(lm, this.frontIsLeft);
    if (c.frontFoot) this.front++;
    if (c.rearFoot) this.rear++;
    if (c.head) this.head++;
  }

  report() {
    const n = Math.max(1, this.frames);
    return { frames: this.frames, front: this.front / n, rear: this.rear / n, head: this.head / n };
  }
}
